import React, { useState, useEffect } from 'react';
import axios from 'axios';
import AppShell from '../components/AppShell';
import { useAuth } from '../context/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

const CHECKS = [
  {
  key: 'appraisal_check',
  emoji: '🏠',
  label: 'Appraisal',
  checking: 'Verifying appraisal is complete and meets requirements',
  pass: 'Appraisal complete value confirmed',
  fail: 'Appraisal issue — contact the Doorly team',
  },
  {
  key: 'title_check',
  emoji: '📋',
  label: 'Title',
  checking: 'Reviewing title search and insurance',
  pass: 'Title clear no liens or encumbrances',
  fail: 'Title issue found action required',
  // TODO: Integrate with title company API for real-time title status
  },
  {
  key: 'insurance_check',
  emoji: '🛡️',
  label: 'Insurance',
  checking: 'Verifying homeowners insurance is bound',
  pass: 'Insurance verified and active',
  fail: 'Insurance not yet verified upload or select a policy',
  },
  {
  key: 'compliance_check',
  emoji: '✅',
  label: 'Compliance',
  checking: 'Running compliance verification',
  pass: 'All compliance requirements met',
  fail: 'Compliance issue — contact the Doorly team',
  // TODO: Integrate compliance checking system
  },
  {
  key: 'funds_check',
  emoji: '💰',
  label: 'Funds',
  checking: 'Verifying closing funds received',
  pass: 'Funds received and verified',
  fail: 'Funds not yet received please wire closing funds',
  },
];

export default function FinalReview() {
  const { user, token } = useAuth();

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [animatedIndex, setAnimatedIndex] = useState(-1);
  const [animating, setAnimating] = useState(false);
  const [showCTC, setShowCTC] = useState(false);

  useEffect(() => {
  const fetchChecks = async () => {
  try {
  const res = await axios.get(`${API_URL}/api/final-review/checks`, {
  headers: { Authorization: `Bearer ${token}` },
  });
  setData(res.data);
  } catch (err) {
  console.error('Failed to fetch final review checks:', err);
  } finally {
  setLoading(false);
  }
  };
  fetchChecks();
  }, [token]);

  useEffect(() => {
  if (!data) return;
  let idx = 0;
  const animate = () => {
  if (idx >= CHECKS.length) {
  const allPass = CHECKS.every(c => data[c.key] === 'pass');
  if (allPass || data.clear_to_close) setShowCTC(true);
  return;
  }
  setAnimating(true);
  setAnimatedIndex(idx);
  setTimeout(() => {
  setAnimating(false);
  idx++;
  setTimeout(animate, 300);
  }, 900);
  };
  setTimeout(animate, 600);
  }, [data]);


  const allRevealed = animatedIndex >= CHECKS.length - 1 && !animating;
  const allPass = data && CHECKS.every(c => data[c.key] === 'pass');
  const failedChecks = data ? CHECKS.filter(c => data[c.key] === 'fail') : [];


  const renderCheckStatus = (check, i) => {
  const isBeforeAnimated = animatedIndex < i;
  const isCurrentlyAnimating = animatedIndex === i && animating;
  const isRevealed = animatedIndex > i || (animatedIndex === i && !animating);
  const status = data ? data[check.key] : null;

  if (isBeforeAnimated) {
  return (
  <div className="flex items-center gap-2">
  <div className="w-3 h-3 rounded-full bg-gray-300" />
  <span className="text-sm text-gray-400">Queued...</span>
  </div>
  );
  }

  if (isCurrentlyAnimating) {
  return (
  <div className="flex items-center gap-2">
  <svg
  className="animate-spin w-5 h-5 text-blue-500"
  xmlns="http://www.w3.org/2000/svg"
  fill="none"
  viewBox="0 0 24 24"
  >
  <circle
  className="opacity-25"
  cx="12"
  cy="12"
  r="10"
  stroke="currentColor"
  strokeWidth="4"
  />
  <path
  className="opacity-75"
  fill="currentColor"
  d="M4 12a8 8 0 018-8v8H4z"
  />
  </svg>
  <span className="text-sm text-blue-500 font-medium">{check.checking}</span>
  </div>
  );
  }

  if (isRevealed) {
  if (status === 'pass') {
  return (
  <div className="flex items-center gap-2">
  <div className="w-6 h-6 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#acf44e" strokeWidth="3">
  <path d="M5 13l4 4L19 7" />
  </svg>
  </div>
  <span className="text-sm text-green-700 font-medium">{check.pass}</span>
  </div>
  );
  }

  if (status === 'fail') {
  const notes = data[`${check.key}_notes`];
  return (
  <div className="flex items-start gap-2">
  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0 mt-0.5">
  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#ff082f" strokeWidth="3">
  <path d="M18 6L6 18M6 6l12 12" />
  </svg>
  </div>
  <div>
  <span className="text-sm text-red-700 font-medium">{check.fail}</span>
  {notes && <p className="text-xs text-red-500 mt-0.5">{notes}</p>}
  </div>
  </div>
  );
  }

  if (status === 'pending') {
  return (
  <div className="flex items-center gap-2">
  <div className="w-3 h-3 rounded-full bg-yellow-400 flex-shrink-0" />
  <span className="text-sm text-yellow-700 font-medium">Pending admin review</span>
  </div>
  );
  }

  return (
  <div className="flex items-center gap-2">
  <div className="w-3 h-3 rounded-full bg-gray-300" />
  <span className="text-sm text-gray-400">Unknown status</span>
  </div>
  );
  }

  return null;
  };

  return (
  <AppShell>
  <div className="max-w-2xl mx-auto px-4 py-8">
  {/* Page Header */}
  <div className="mb-8">
  <div className="text-xs font-bold uppercase tracking-widest text-purple-600 mb-3">Stage 12 of 12</div>
  <h1 className="text-2xl sm:text-3xl font-bold text-black mb-2">Final Review</h1>
  <p className="text-gray-500">Our team is completing your final checks before issuing your Clear to Close</p>
  </div>

  {/* Loading State */}
  {loading && (
  <div className="flex flex-col items-center justify-center py-16">
  <svg
  className="animate-spin w-10 h-10 text-blue-500 mb-4"
  xmlns="http://www.w3.org/2000/svg"
  fill="none"
  viewBox="0 0 24 24"
  >
  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8H4z" />
  </svg>
  <p className="text-gray-500 text-sm">Loading your final review status...</p>
  </div>
  )}

  {/* Checks List */}
  {!loading && data && (
  <>
  <div className="space-y-3">
  {CHECKS.map((check, i) => (
  <div
  key={check.key}
  className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4"
  >
  <div className="text-2xl flex-shrink-0">{check.emoji}</div>
  <div className="flex-1 min-w-0">
  <p className="font-semibold text-gray-800 text-sm mb-1">{check.label}</p>
  {renderCheckStatus(check, i)}
  </div>
  </div>
  ))}
  </div>

  {/* Clear to Close Banner */}
  {showCTC && (allPass || data.clear_to_close) && (
  <div className="mt-8 rounded-2xl border-2 border-green-400 bg-green-50 p-8 text-center">
  <div className="text-5xl mb-4">🎉</div>
  <h2 className="text-3xl font-bold text-green-700 mb-2">CLEAR TO CLOSE!</h2>
  <p className="text-green-600 text-lg mb-6">
  Congratulations {user?.firstName}! All checks have passed. You are cleared to close on your new home.
  </p>
  <div className="bg-white rounded-xl p-4 mb-6 text-left max-w-sm mx-auto">
  <p className="text-sm text-gray-500 mb-1">Closing Date</p>
  <p className="font-semibold text-gray-900">{data.closing_date || 'To be scheduled'}</p>
  <p className="text-sm text-gray-500 mt-3 mb-1">Estimated Monthly Payment</p>
  <p className="font-semibold text-gray-900">$1,944/mo</p>
  {/* TODO: Pull real payment from loan file */}
  </div>

  {/* ⚠️ READ-ONLY. This was a "Schedule Your Closing" button opening a booking
      modal, and on submit it rendered "Closing Appointment Confirmed" from
      LOCAL STATE — which did not survive a refresh and described a booking that
      existed nowhere. Borrowers do not set their closing date; Doorly does,
      with title. This now shows what Doorly actually set, or says plainly that
      nothing is set yet. */}
  {data?.scheduled_closing_date ? (
  <div className="mt-4 rounded-xl bg-green-100 border border-green-300 p-4 flex items-start gap-3 text-left max-w-sm mx-auto">
  <span className="text-xl">✅</span>
  <div>
  <p className="font-semibold text-green-700">Closing Appointment Confirmed</p>
  <p className="text-sm text-green-600 mt-1">
  Your closing is scheduled for {new Date(data.scheduled_closing_date).toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
  {data.scheduled_closing_time ? ` at ${data.scheduled_closing_time}` : ''}
  {data.scheduled_closing_location ? ` (${data.scheduled_closing_location})` : ''}.
  {' '}The Doorly team will confirm the details with you.
  </p>
  </div>
  </div>
  ) : (
  /* ⚠️ NOT A FORM, AND NOT A PROMISE OF A DATE. Until Doorly sets one there is
     nothing to show. Saying who does it and what happens next beats a button
     that implies the borrower is the one holding this up. */
  <div className="mt-4 rounded-xl bg-gray-50 border border-gray-200 p-4 text-left max-w-sm mx-auto">
  <p className="font-semibold text-gray-800">Your closing date</p>
  <p className="text-sm text-gray-500 mt-1">
  Doorly schedules your closing with the title company once your file is clear to
  close. We&rsquo;ll confirm the date, time and location with you here and by
  email &mdash; there&rsquo;s nothing for you to book.
  </p>
  </div>
  )}
  </div>
  )}

  {/* Some Checks Failed Banner */}
  {allRevealed && !showCTC && failedChecks.length > 0 && (
  <div className="mt-8 rounded-2xl border-2 border-yellow-300 bg-yellow-50 p-6">
  <div className="text-2xl mb-2">⏳</div>
  <h2 className="text-xl font-bold text-yellow-800 mb-1">Almost There</h2>
  <p className="text-yellow-700 mb-4">
  The following items need attention before we can issue your Clear to Close:
  </p>
  <ul className="space-y-2">
  {failedChecks.map(check => (
  <li key={check.key} className="flex items-start gap-2">
  <span className="text-lg flex-shrink-0">{check.emoji}</span>
  <div>
  <p className="font-semibold text-yellow-800 text-sm">{check.label}</p>
  <p className="text-sm text-yellow-700">{check.fail}</p>
  </div>
  </li>
  ))}
  </ul>
  <p className="text-sm text-yellow-600 mt-4">Estimated time to resolve: 1-2 business days</p>
  </div>
  )}
  </>
  )}

  {/* No Data Fallback */}
  {!loading && !data && (
  <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center">
  <p className="text-gray-500">Unable to load your final review status. Please try again or contact the Doorly team.</p>
  </div>
  )}
  </div>

  {/* ⚠️ THE SCHEDULER MODAL IS REMOVED. It collected a date, time, location and
      special requests and POSTed them to /api/final-review/schedule-closing,
      which wrote final_review.closing_* and nothing else — no
      closing_appointments row, no users.date_closing, no calendar entry, and
      nobody notified. Verified before removing: final_review held ZERO rows, so
      no borrower ever used it. The endpoint is gone too; see
      backend/routes/final-review.js. */}
  </AppShell>
  );
}
